/**
 * ChipStrip — Row 2 of the new header.
 *
 * Project + Branch dropdowns (preserved from old HeaderProjectSwitcher) on
 * the left, system-state chips in the middle, profile + quick-help on the
 * right. Each chip is self-contained; this file is purely composition.
 */
import React from 'react';
import {
  Box,
  Chip,
  Divider,
  ListItemIcon,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import { Logout, Settings, ScienceOutlined } from '@mui/icons-material';
import ProjectChip from './chips/ProjectChip';
import BranchChip from './chips/BranchChip';
import SearchChip from './chips/SearchChip';
import NotificationsChip from './chips/NotificationsChip';
import HelpButton from '../help/HelpButton';
import CreditBalanceBadge from '../billing/CreditBalanceBadge';

const ChipStrip = ({
  projects,
  selectedProject,
  onProjectChange,
  branches,
  currentBranch,
  onBranchChange,
  recentProjectIds,
  user,
  currentUser,
  isDevMode,
  onProfileSettings,
  onSignOut,
  onSearchPick,
  modeSlug,
  modeName,
}) => {
  const [userMenuAnchor, setUserMenuAnchor] = React.useState(null);
  const hasProject = !!selectedProject;
  // eslint-disable-next-line no-unused-vars -- retained (WIP)
  const projectId = selectedProject?.project_id;
  // eslint-disable-next-line no-unused-vars -- retained (WIP)
  const branchId = currentBranch;

  // Dual-mode (2026-07-16) — show the active project's vertical mode. Only the
  // two dual-mode profiles surface a chip; legacy verticals stay unlabeled.
  const MODE_LABELS = { medtech: 'Medtech', pharma_gxp: 'Pharma GxP' };
  const modeLabel = MODE_LABELS[modeSlug];

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        height: 44,
        px: 1.5,
        bgcolor: 'background.paper',
      }}
    >
      <ProjectChip
        projects={projects}
        selectedProject={selectedProject}
        onProjectChange={onProjectChange}
        recentProjectIds={recentProjectIds}
      />
      <BranchChip
        branches={branches}
        currentBranch={currentBranch}
        onBranchChange={onBranchChange}
        hasProject={hasProject}
      />
      {hasProject && modeLabel && (
        <Tooltip title={modeName || `Mode: ${modeLabel}`} placement="bottom">
          <Chip
            size="small"
            icon={<ScienceOutlined sx={{ fontSize: 15 }} />}
            label={modeLabel}
            variant="outlined"
            color={modeSlug === 'pharma_gxp' ? 'secondary' : 'primary'}
            sx={{ height: 24, fontWeight: 600 }}
          />
        </Tooltip>
      )}
      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
      <SearchChip onPick={onSearchPick} />

      <Box sx={{ flexGrow: 1 }} />

      {isDevMode && (
        <Box
          sx={{
            px: 1,
            py: 0.25,
            borderRadius: 1,
            bgcolor: 'rgba(255, 152, 0, 0.1)',
            border: '1px solid rgba(255, 152, 0, 0.3)',
          }}
        >
          <Typography variant="caption" sx={{ color: '#f57c00', fontWeight: 600 }}>
            DEV MODE
          </Typography>
        </Box>
      )}

      <CreditBalanceBadge />
      <NotificationsChip />
      <HelpButton />

      <Box
        data-tour="user-menu"
        onClick={(e) => setUserMenuAnchor(e.currentTarget)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1,
          py: 0.5,
          borderRadius: 2,
          bgcolor: 'rgba(0, 0, 0, 0.04)',
          border: '1px solid',
          borderColor: 'divider',
          cursor: 'pointer',
          '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.08)' },
        }}
      >
        {user?.avatar_url ? (
          <Box
            component="img"
            src={user.avatar_url}
            alt={currentUser?.name || 'user'}
            sx={{ width: 24, height: 24, borderRadius: '50%' }}
          />
        ) : (
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.7rem',
              fontWeight: 600,
              color: 'white',
            }}
          >
            {currentUser?.name?.charAt(0)?.toUpperCase() || '?'}
          </Box>
        )}
        <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500, fontSize: '0.8125rem' }}>
          {currentUser?.name || user?.username || '…'}
        </Typography>
      </Box>

      <Menu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={() => setUserMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {user?.username || currentUser?.name || 'User'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {user?.email || currentUser?.email}
          </Typography>
        </Box>
        <Divider />
        <MenuItem onClick={() => { setUserMenuAnchor(null); onProfileSettings?.(); }}>
          <ListItemIcon><Settings fontSize="small" /></ListItemIcon>
          Settings
        </MenuItem>
        <MenuItem onClick={() => { setUserMenuAnchor(null); onSignOut?.(); }}>
          <ListItemIcon><Logout fontSize="small" /></ListItemIcon>
          Sign out
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default ChipStrip;
